import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SaveIndicatorProps {
  isSaving: boolean;
  lastSaved?: Date | null;
  error?: string | null;
}

const SaveIndicator: React.FC<SaveIndicatorProps> = ({ isSaving, lastSaved, error }) => {
  const [showSaved, setShowSaved] = React.useState(false);
  
  // Enhanced debug logging
  React.useEffect(() => {
    console.log('[SaveIndicator] Component mounted', { 
      isSaving, 
      lastSaved, 
      error,
      timestamp: new Date().toISOString()
    });
    
    // Check if component is actually in the DOM
    const checkVisibility = () => {
      const elements = document.querySelectorAll('[data-save-indicator]');
      console.log('[SaveIndicator] DOM check:', {
        elementsFound: elements.length,
        firstElement: elements[0],
        computedStyles: elements[0] ? window.getComputedStyle(elements[0]) : null
      });
    };
    
    // Check after a short delay to ensure DOM is updated
    setTimeout(checkVisibility, 100);
  }, []);

  React.useEffect(() => {
    console.log('[SaveIndicator] Props changed:', { 
      isSaving, 
      lastSaved, 
      error,
      showSaved,
      shouldRender: isSaving || showSaved || error
    });
    
    if (!isSaving && lastSaved && !error) {
      setShowSaved(true);
      const timer = setTimeout(() => setShowSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isSaving, lastSaved, error]);

  // Debug: Always show something for testing
  const forceShow = process.env.NODE_ENV === 'development' && true; // Force visibility in development
  
  return (
    <>
      {/* Debug button in development */}
      {process.env.NODE_ENV === 'development' && (
        <button
          onClick={() => {
            console.log('[SaveIndicator] Manual trigger test');
            setShowSaved(true);
            setTimeout(() => setShowSaved(false), 3000);
          }}
          className="fixed bottom-4 right-4 z-50 px-2 py-1 bg-purple-600 text-white text-xs rounded"
          data-save-indicator-debug="true"
        >
          Test Save Indicator
        </button>
      )}
      
      <AnimatePresence>
        {(isSaving || showSaved || error || forceShow) && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 right-4 z-50"
          data-save-indicator="true"
          style={{ 
            // Explicit styles to ensure visibility
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 9999
          }}
        >
          <div className={`
            px-4 py-2 rounded-lg shadow-lg backdrop-blur-sm
            ${error ? 'bg-red-900/80 text-red-100 border border-red-600' : 
              isSaving ? 'bg-blue-900/80 text-blue-100 border border-blue-600' : 
              'bg-green-900/80 text-green-100 border border-green-600'}
          `}
          style={{
            // Additional explicit styles for debugging
            minWidth: '120px',
            minHeight: '40px',
            display: 'block',
            opacity: 1,
            visibility: 'visible'
          }}>
            <div className="flex items-center space-x-2">
              {isSaving && (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                  <span className="text-sm font-medium">Saving...</span>
                </>
              )}
              {showSaved && !error && (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">Saved</span>
                </>
              )}
              {error && (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">Save failed</span>
                </>
              )}
              {forceShow && !isSaving && !showSaved && !error && (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium">Auto-save ready</span>
                </>
              )}
            </div>
            {lastSaved && showSaved && !error && (
              <div className="text-xs text-green-300 mt-1">
                Last saved: {new Date(lastSaved).toLocaleTimeString()}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export { SaveIndicator };